import {AfterViewInit, Component, forwardRef, inject, Injector, signal, Type, ViewChild} from '@angular/core';
import {NgComponentOutlet} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, Subject} from 'rxjs';
import {Modal} from '@shared/components/common/modal/modal';
import {DialogService, OpenFormOptions} from '@shared/services/dialog.service';
import {Cliente} from '../clientes.interface';
import {DatosFacturacion} from '../../datos-facturacion/datos-facturacion';
import {ContactosAdministrativos} from '../../contactos-administrativos/contactos-administrativos';
import {ContratosCliente} from '../contratos-cliente/contratos-cliente';
import {Objetivos} from '../objetivos/objetivos';
import {Presupuestos} from '../presupuestos/presupuestos';

export type SeccionCliente = 'facturacion' | 'contactos' | 'contrato' | 'objetivos' | 'presupuestos';

export interface SeccionClienteModalData {
  seccion: SeccionCliente;
  clienteId: number;
  cliente: Cliente | null;
  /** Abre directo el formulario de alta (botón + de la ficha); al cerrarlo se cierra el modal. */
  nuevo?: boolean;
}

const TITULOS: Record<SeccionCliente, string> = {
  facturacion: 'Datos de facturación',
  contactos: 'Contactos',
  contrato: 'Contratos',
  objetivos: 'Objetivos de producción',
  presupuestos: 'Presupuestos',
};

interface FormularioEnLinea {
  component: Type<unknown>;
  injector: Injector;
}

/**
 * Cada línea de resumen de la ficha del cliente abre su sección completa acá.
 *
 * Las listas de cada sección abren sus formularios con `DialogService.openForm`.
 * Este modal se provee a sí mismo como `DialogService`, así que esos formularios
 * reemplazan a la lista dentro del mismo modal en vez de apilar otro encima. Al
 * cerrarse el formulario se vuelve a la lista (que sigue viva, oculta) y esta
 * recarga como siempre con `afterClosed()`.
 */
@Component({
  selector: 'app-seccion-cliente-modal',
  imports: [NgComponentOutlet, Modal, DatosFacturacion, ContactosAdministrativos, ContratosCliente, Objetivos, Presupuestos],
  providers: [{provide: DialogService, useExisting: forwardRef(() => SeccionClienteModal)}],
  template: `
    <div [class.hidden]="formulario() || data.nuevo">
      <app-modal [title]="titulo" [showCloseButton]="true" [scrollable]="true" (onCloseClick)="dialogRef.close()">
        @switch (data.seccion) {
          @case ('facturacion') {
            @if (data.cliente; as cliente) {
              <!-- Al guardar se cierra: la ficha recarga el cliente y su resumen -->
              <app-datos-facturacion
                [datoFacturacion]="cliente.persona.dato_facturacion"
                [cliente]="cliente"
                [perPersonaId]="cliente.per_persona_id"
                [clienteId]="data.clienteId"
                (referesh)="dialogRef.close(true)"
                (cancelar)="dialogRef.close()"
              />
            }
          }
          @case ('contactos') { <app-contactos-administrativos #lista [cliClienteId]="data.clienteId" /> }
          @case ('contrato') { <app-contratos-cliente #lista [clienteId]="data.clienteId" /> }
          @case ('objetivos') { <app-objetivos #lista [clienteId]="data.clienteId" /> }
          @case ('presupuestos') { <app-presupuestos #lista [clienteId]="data.clienteId" /> }
        }
      </app-modal>
    </div>

    @if (formulario(); as f) {
      <ng-container *ngComponentOutlet="f.component; injector: f.injector" />
    }
  `,
})
export class SeccionClienteModal implements AfterViewInit {
  data: SeccionClienteModalData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<SeccionClienteModal>);

  formulario = signal<FormularioEnLinea | null>(null);

  @ViewChild('lista') private lista?: {openCreateModal(): void};

  private injector = inject(Injector);
  // El DialogService de verdad, para que los formularios abran sus propios diálogos normalmente
  private dialogService = inject(DialogService, {skipSelf: true});

  get titulo(): string {
    return TITULOS[this.data.seccion];
  }

  ngAfterViewInit(): void {
    if (this.data.nuevo) {
      queueMicrotask(() => this.lista?.openCreateModal());
    }
  }

  /** Reemplaza a `DialogService.openForm` para las listas de la sección. */
  openForm<T>(component: Type<T>, options: OpenFormOptions = {}): MatDialogRef<T> {
    const cerrado = new Subject<unknown>();
    const ref = {
      close: (resultado?: unknown) => {
        this.formulario.set(null);
        cerrado.next(resultado);
        cerrado.complete();
        if (this.data.nuevo) this.dialogRef.close(resultado);
      },
      afterClosed: (): Observable<unknown> => cerrado.asObservable(),
    };

    this.formulario.set({
      component,
      injector: Injector.create({
        parent: this.injector,
        providers: [
          {provide: MAT_DIALOG_DATA, useValue: options.data},
          {provide: MatDialogRef, useValue: ref},
          {provide: DialogService, useValue: this.dialogService},
        ],
      }),
    });

    return ref as unknown as MatDialogRef<T>;
  }
}
